import { useEffect, useMemo, useState } from 'react';
import { RefreshCw } from 'lucide-react';
import { Button, Chip, ChipGroup, Select, TextField } from '@nyce/admin-kit/ui';
import { useDebounced } from '@nyce/admin-kit/console';
import {
  ActionsRow,
  ConfirmButton,
  EmptyState,
  ErrorBox,
  Loading,
  Page,
  TableScroller,
  useAsync,
  useNotice,
} from '../../components/common';
import { formatDateTime, formatRelative } from '../../lib/format';
import { useParam } from '../../pages/statistics/shared';
import { saveBlob } from '../../buddy/api/client';
import { JOB_STATUSES, OPEN_STATUSES, printQueue, type JobStatus, type PrintJob } from '../api/client';
import { usePrintQueueLinks } from '../links';
import { JobPreview, jobBytes, previewKind } from './JobPreview';
import {
  JobStatusChip,
  TenantCell,
  isStuck,
  tenantLabel,
  usePrintQueueTarget,
  usePrintQueueWrite,
} from '../shared';
import '../printqueue.css';

const PAGE = 100;

/** Stuck jobs are filtered here from bigger pages of unfinished ones (the print queue has no age filter). */
const STUCK_PAGE = 1000;

/**
 * `?status=` = job statuses, comma separated (none = every status; `open` = every unfinished one, as the dashboard
 * links), plus `stuck` = only unfinished jobs untouched for 15 min (of the picked statuses, else of the open ones).
 */
function parseStatusParam(param: string): { statuses: JobStatus[]; stuck: boolean } {
  const tokens = param.split(',').map((t) => t.trim());
  const picked = new Set(
    tokens.flatMap((t) => (t === 'open' ? OPEN_STATUSES : JOB_STATUSES.filter((s) => s === t)))
  );
  return { statuses: JOB_STATUSES.filter((s) => picked.has(s)), stuck: tokens.includes('stuck') };
}

/** The WorkServer's print types (FastReport, PDF, raw printer data), offered before any job showed its own. */
const KNOWN_TYPES = ['FR', 'PDF', 'RAW'];

/**
 * The type / file name filters, also applied to the page here: print queue 1.10.0 filters on the server, older ones
 * ignore both (and list no file names).
 */
function matchesJob(j: PrintJob, type: string, file: string) {
  if (type && (j.printType ?? '').toLowerCase() !== type.toLowerCase()) return false;
  return !file || (j.fileName ?? '').toLowerCase().includes(file.trim().toLowerCase());
}

const statusParam = (statuses: JobStatus[], stuck: boolean) =>
  [...JOB_STATUSES.filter((s) => statuses.includes(s)), ...(stuck ? ['stuck'] : [])].join(',');

/** Base64 print data → a file named after the job. */
function download(job: PrintJob) {
  const bytes = jobBytes(job);
  const type = (job.printType || '').toLowerCase();
  const ext = type === 'pdf' ? 'pdf' : type === 'fr' ? 'fpx' : type === 'raw' ? 'prn' : 'bin';
  const name = job.fileName || `print-job-${job.id}.${ext}`;
  saveBlob(new Blob([bytes], { type: ext === 'pdf' ? 'application/pdf' : 'application/octet-stream' }), name);
}

/** Print jobs of one environment, newest first: filter, page, re-queue, mark, delete, download. */
export function PrintQueueJobsPage() {
  const target = usePrintQueueTarget();
  const api = useMemo(() => printQueue(target), [target]);
  const canWrite = usePrintQueueWrite();
  const links = usePrintQueueLinks(target);
  const [tenant, setTenant] = useParam('tenant', '');
  const [server, setServer] = useParam('server', '');
  const [statusKey, setStatusKey] = useParam('status', '');
  const [type, setType] = useParam('type', '');
  const [file, setFile] = useParam('file', '');
  const [fileDraft, setFileDraft] = useState(file);
  const settledFile = useDebounced(fileDraft.trim());
  useEffect(() => {
    if (settledFile !== file) setFile(settledFile);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settledFile]);
  useEffect(() => setFileDraft((d) => (d.trim() === file ? d : file)), [file]);
  // every type seen in this environment, so the picker keeps them while one is picked
  const [seenTypes, setSeenTypes] = useState<string[]>([]);
  const { statuses: picked, stuck: stuckOnly } = parseStatusParam(statusKey);
  const statuses = picked.length ? picked : stuckOnly ? OPEN_STATUSES : undefined;
  const printers = useAsync(() => api.printers(), [api]);
  const [jobs, setJobs] = useState<PrintJob[]>([]);
  const [more, setMore] = useState(false);
  // id the next page continues after (the last fetched job, which the stuck filter may hide)
  const [cursor, setCursor] = useState<number>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState<number | null>(null);
  const toast = useNotice();
  const filterKey = `${target}|${tenant}|${server}|${statusKey}|${type}|${file}`;

  const load = async (after?: number) => {
    setLoading(true);
    setError(null);
    try {
      const page = await api.jobs({
        tenantId: tenant || undefined,
        printServerName: server || undefined,
        statuses,
        printType: type || undefined,
        fileName: file || undefined,
        size: stuckOnly ? STUCK_PAGE : PAGE,
        lastPageId: after,
      });
      const shown = page.filter((j) => (!stuckOnly || isStuck(j)) && matchesJob(j, type, file));
      setSeenTypes((cur) => {
        const add = page
          .map((j) => j.printType)
          .filter((t) => t && !cur.some((c) => c.toLowerCase() === t.toLowerCase()));
        return add.length ? [...cur, ...new Set(add)] : cur;
      });
      setJobs((cur) => (after ? [...cur, ...shown] : shown));
      setCursor(page[page.length - 1]?.id);
      setMore(page.length === (stuckOnly ? STUCK_PAGE : PAGE));
      if (!after) setSelected(new Set());
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterKey]);

  const tenants = useMemo(() => {
    const ids = new Set([...(printers.data ?? []).map((p) => p.tenantId), ...jobs.map((j) => j.tenantId)]);
    if (tenant) ids.add(tenant);
    return [...ids].sort((a, b) => a.localeCompare(b));
  }, [printers.data, jobs, tenant]);
  const types = useMemo(() => {
    const all = [...KNOWN_TYPES, ...seenTypes, ...(type ? [type] : [])];
    const unique = all.filter((t, i) => all.findIndex((o) => o.toLowerCase() === t.toLowerCase()) === i);
    return unique.sort((a, b) => a.localeCompare(b));
  }, [seenTypes, type]);
  const servers = useMemo(() => {
    const ids = new Set(
      (printers.data ?? []).filter((p) => !tenant || p.tenantId === tenant).map((p) => p.printServerName)
    );
    if (server) ids.add(server);
    return [...ids].sort((a, b) => a.localeCompare(b));
  }, [printers.data, tenant, server]);

  const run = async (what: string, fn: () => Promise<unknown>) => {
    setBusy(true);
    try {
      await fn();
      toast.success(what);
      await load();
    } catch (e) {
      toast.error((e as Error).message);
    } finally {
      setBusy(false);
    }
  };
  const setStatus = (ids: number[], status: JobStatus) =>
    run(`${ids.length === 1 ? `Job ${ids[0]}` : `${ids.length} jobs`} set to ${status}`, async () => {
      for (const id of ids) await api.setStatus(id, status);
    });
  const remove = (ids: number[]) =>
    run(`Deleted ${ids.length === 1 ? `job ${ids[0]}` : `${ids.length} jobs`}`, () => api.deleteJobs(ids));
  const fetchFile = async (id: number) => {
    try {
      download(await api.job(id));
    } catch (e) {
      toast.error((e as Error).message);
    }
  };
  const toggle = (id: number) =>
    setSelected((cur) => {
      const next = new Set(cur);
      if (!next.delete(id)) next.add(id);
      return next;
    });
  const allOn = jobs.length > 0 && jobs.every((j) => selected.has(j.id));
  const ids = [...selected];
  const cols = canWrite ? 7 : 6;

  return (
    <Page
      title="Print jobs"
      description="Jobs the WorkServers queued for the print servers, newest first. Printed jobs are removed by the print queue after a while."
      actions={
        <Button
          size="sm"
          variant="ghost"
          icon={<RefreshCw size={14} />}
          loading={loading}
          onClick={() => void load()}
        >
          Refresh
        </Button>
      }
    >
      {toast.element}
      <div className="pq-toolbar">
        <Select
          aria-label="Tenant"
          value={tenant}
          onChange={(v) => {
            setTenant(v);
            setServer('');
          }}
          options={[
            { value: '', label: 'All tenants' },
            ...tenants.map((t) => ({ value: t, label: tenantLabel(t, links) })),
          ]}
        />
        <Select
          aria-label="Print server"
          value={server}
          onChange={setServer}
          options={[
            { value: '', label: 'All print servers' },
            ...servers.map((s) => ({ value: s, label: s })),
          ]}
        />
        <Select
          aria-label="Type"
          value={types.find((t) => t.toLowerCase() === type.toLowerCase()) ?? ''}
          onChange={setType}
          options={[{ value: '', label: 'All types' }, ...types.map((t) => ({ value: t, label: t }))]}
        />
        <TextField
          aria-label="File name"
          className="nyk-search"
          type="search"
          placeholder="File name contains"
          value={fileDraft}
          onChange={(e) => setFileDraft(e.target.value)}
        />
        <div className="pq-status-filter">
          <ChipGroup
            aria-label="Status"
            options={JOB_STATUSES.map((st) => ({ value: st, label: st }))}
            value={picked}
            onChange={(v) => setStatusKey(statusParam(v as JobStatus[], stuckOnly))}
          />
          <Chip
            size="sm"
            selected={stuckOnly}
            onClick={() => setStatusKey(statusParam(picked, !stuckOnly))}
            title="Unfinished jobs untouched for more than 15 minutes"
          >
            Stuck (over 15 min)
          </Chip>
        </div>
      </div>
      <ErrorBox error={error ?? printers.error} />
      {canWrite && ids.length > 0 && (
        <div className="pq-bulkbar" data-testid="pq-bulkbar">
          <span>{ids.length} selected</span>
          <Button
            size="sm"
            variant="secondary"
            loading={busy}
            onClick={() => void setStatus(ids, 'Registered')}
          >
            Re-queue
          </Button>
          <ConfirmButton
            size="sm"
            variant="danger"
            loading={busy}
            message={`Delete ${ids.length} print jobs? They will not be printed.`}
            onConfirm={() => void remove(ids)}
          >
            Delete
          </ConfirmButton>
        </div>
      )}
      <div className="nyk-card">
        <Loading loading={loading && !jobs.length} />
        {!!jobs.length && (
          <TableScroller>
            <table className="nyk-ui-table nyk-cards" data-testid="pq-jobs">
              <thead>
                <tr>
                  {canWrite && (
                    <th>
                      <input
                        type="checkbox"
                        className="pq-check"
                        aria-label="Select all"
                        checked={allOn}
                        onChange={() => setSelected(allOn ? new Set() : new Set(jobs.map((j) => j.id)))}
                      />
                    </th>
                  )}
                  <th>Job</th>
                  <th>Tenant</th>
                  <th>Print server · printer</th>
                  <th className="nyk-ui-num">Copies</th>
                  <th>Status</th>
                  <th>Added</th>
                </tr>
              </thead>
              {jobs.map((j) => {
                const stuck = isStuck(j);
                return (
                  <tbody key={j.id} className="nyk-row-group" data-job={j.id}>
                    <tr>
                      {canWrite && (
                        <td>
                          <input
                            type="checkbox"
                            className="pq-check"
                            aria-label={`Select job ${j.id}`}
                            checked={selected.has(j.id)}
                            onChange={() => toggle(j.id)}
                          />
                        </td>
                      )}
                      <td className="nyk-card-title">
                        <b>#{j.id}</b> {j.fileName && <span>{j.fileName}</span>}
                        <div className="nyk-ui-hint">{j.printType}</div>
                      </td>
                      <td data-label="Tenant">
                        <TenantCell tenantId={j.tenantId} links={links} target={target} />
                      </td>
                      <td data-label="Print server · printer">
                        {j.printServerName}
                        <div className="nyk-ui-hint">{j.printerName}</div>
                      </td>
                      <td className="nyk-ui-num" data-label="Copies">
                        {j.copies}
                      </td>
                      <td data-label="Status">
                        <JobStatusChip status={j.status} />
                        {stuck && (
                          <div className="nyk-text-amber" title="Unfinished for more than 15 minutes">
                            stuck {formatRelative(j.modificationDate ?? j.dateAdded)}
                          </div>
                        )}
                        {!stuck && j.modificationDate && (
                          <div className="nyk-ui-hint">{formatRelative(j.modificationDate)}</div>
                        )}
                      </td>
                      <td data-label="Added">{formatDateTime(j.dateAdded)}</td>
                    </tr>
                    <ActionsRow colSpan={cols}>
                      {previewKind(j.printType) && (
                        <Button size="sm" variant="ghost" onClick={() => setPreview(j.id)}>
                          Preview
                        </Button>
                      )}
                      <Button size="sm" variant="ghost" onClick={() => void fetchFile(j.id)}>
                        Download
                      </Button>
                      {canWrite && j.status !== 'Registered' && (
                        <Button
                          size="sm"
                          variant="ghost"
                          disabled={busy}
                          onClick={() => void setStatus([j.id], 'Registered')}
                        >
                          Re-queue
                        </Button>
                      )}
                      {canWrite && j.status !== 'Printed' && (
                        <Button
                          size="sm"
                          variant="ghost"
                          disabled={busy}
                          onClick={() => void setStatus([j.id], 'Printed')}
                        >
                          Mark printed
                        </Button>
                      )}
                      {canWrite && (
                        <ConfirmButton
                          size="sm"
                          variant="ghost"
                          disabled={busy}
                          message={`Delete print job ${j.id}? It will not be printed.`}
                          onConfirm={() => void remove([j.id])}
                        >
                          Delete
                        </ConfirmButton>
                      )}
                    </ActionsRow>
                  </tbody>
                );
              })}
            </table>
          </TableScroller>
        )}
        {!loading && !error && !jobs.length && <EmptyState title="No print jobs match." />}
        {preview != null && (
          <JobPreview
            jobId={preview}
            load={api.job}
            loadPdf={api.jobPdf}
            loadLabels={api.jobLabels}
            onDownload={download}
            onClose={() => setPreview(null)}
          />
        )}
        {more && (
          <div className="pq-more">
            <Button size="sm" variant="ghost" loading={loading} onClick={() => void load(cursor)}>
              Load older
            </Button>
          </div>
        )}
      </div>
    </Page>
  );
}
